import { TalentSearchFiltersProps } from '@type/filters';
import { useResumeSearchAlerts } from '@hooks/filters/useResumeSearchAlerts';
import { Drawer } from '@components/common';
import { useBjModal } from '@hooks/common/useBjModal';
import { useApplicantFilterDrawer } from '@components/contexts/ApplicantFilterDrawerContext';
import { TopBarTalentSearchFiltersHub } from '@components/features/filters/hubs/TopBarTalentSearchFiltersHub';
import { SidebarTalentSearchFiltersHub } from './hubs/SidebarTalentSearchFiltersHub';


/**
 * @description The talent search filters component. It contains the top bar filters and the sidebar filters.
 * * **queryParams** - the query params object.
 * * **setQueryParams** - the function to set the query params.
 * * **applicationStats** - the application stats (viewed, interview, new, etc.) for the job applicants detail page; optional.
 * * **jobSlug** - the job slug (for applicants list); optional.
 * * **locale** - the locale of the app.
 * * **jobBasicInfo** - the basic info for the job (for applicants list); optional.
 * * **hideApplicationNumbers** - whether to hide the application numbers in the filters; optional.
 * * **boardInfo** - the board info object, containing total candidates and isFromBoard; optional.
 */
export const TalentSearchFilters = (props: TalentSearchFiltersProps) => {
  // Destructure props
  const {
    queryParams,
    setQueryParams,
    applicationStats,
    boardInfo,
    jobSlug,
    hideApplicationNumbers,
    hideTopBar,
    hideSidebarButton,
    hideOrderBy,
    locale,
    jobBasicInfo,
  } = props;

  // Drawer states & functions
  // When ApplicantFilterDrawerContext is present (Mission Control inline view) use it
  // so an external trigger can control the same drawer. Outside that context the
  // behaviour is identical to before.
  const externalDrawer = useApplicantFilterDrawer();
  const { isOpen: internalOpen, toggleModal: internalToggle } = useBjModal();
  const drawerOpen = externalDrawer ? externalDrawer.isOpen : internalOpen;
  const toggleDrawer = externalDrawer ? externalDrawer.toggleDrawer : internalToggle;


  // If not in job context, refresh the Saved Searches when the drawer is closed
  // **********************************************
  const { setOnOpenFilters, setOnCloseFilters } = useResumeSearchAlerts({ locale });

  // Render component
  // **********************************************
  return (
    <>
      {/* TOP BAR FILTERS */}
      {!hideTopBar && (
        <div className="border-y border-input/50 bg-surface-50">
          <div className="container">
            <div className="flex items-center justify-between gap-6">
              <div className="scrollbar-thin relative flex min-h-13 grow items-start gap-2 overflow-x-auto pt-2">
                <TopBarTalentSearchFiltersHub
                  queryParams={queryParams}
                  setQueryParams={setQueryParams}
                  toggleDrawer={toggleDrawer}
                  applicationStats={applicationStats}
                  hideApplicationNumbers={hideApplicationNumbers}
                  jobSlug={jobSlug}
                  locale={locale}
                  jobBasicInfo={jobBasicInfo}
                  boardInfo={boardInfo}
                  hideSidebarButton={hideSidebarButton}
                  hideOrderBy={hideOrderBy}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SIDEBAR FILTERS */}
      <Drawer
        isOpen={drawerOpen}
        onClose={toggleDrawer}
        position="right"
        afterEnter={() => { setOnOpenFilters(queryParams); }}
        beforeLeave={() => { setOnCloseFilters(queryParams); }}
      >
        <SidebarTalentSearchFiltersHub
          toggleDrawer={toggleDrawer}
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          jobBasicInfo={jobBasicInfo}
        />
      </Drawer>

    </>
  );
};
